package com.fantasytenis.web.components.skills

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.dom.Div
import com.fantasytenis.web.theme.Colors

private const val SVG_NS = "http://www.w3.org/2000/svg"

/**
 * Sparkline component for showing skill trends over time
 *
 * @param values List of values (newest first)
 * @param color Line color
 * @param width Chart width
 * @param height Chart height
 * @param showDots Whether to show dots at each data point
 */
@Composable
fun Sparkline(
    values: List<Int>,
    color: CSSColorValue = Colors.Primary,
    width: Int = 80,
    height: Int = 24,
    showDots: Boolean = false
) {
    if (values.isEmpty()) return

    // Reverse values so oldest is on left
    val reversedValues = values.reversed()

    val minValue = 1
    val maxValue = 12
    val valueRange = maxValue - minValue

    // Calculate points
    val stepX = width.toDouble() / (reversedValues.size - 1).coerceAtLeast(1)
    val points = reversedValues.mapIndexed { index, value ->
        val x = index * stepX
        val normalizedY = (value - minValue).toDouble() / valueRange
        val y = height - (normalizedY * height * 0.8) - (height * 0.1) // 10% padding top/bottom
        Pair(x, y)
    }

    // Build SVG path
    val pathData = points.mapIndexed { index, (x, y) ->
        if (index == 0) "M $x $y" else "L $x $y"
    }.joinToString(" ")

    // Convert color to hex string for SVG
    val colorHex = "#00A651" // Primary green

    Div {
        Div({
            attr("data-sparkline", "true")
        }) {
            DisposableEffect(values, width, height) {
                val container = scopeElement

                // Clear existing SVG
                while (container.firstChild != null) {
                    container.removeChild(container.firstChild!!)
                }

                // Create SVG element
                val svg = document.createElementNS(SVG_NS, "svg").apply {
                    setAttribute("width", "$width")
                    setAttribute("height", "$height")
                    setAttribute("viewBox", "0 0 $width $height")
                }

                // Line
                val path = document.createElementNS(SVG_NS, "path").apply {
                    setAttribute("d", pathData)
                    setAttribute("fill", "none")
                    setAttribute("stroke", colorHex)
                    setAttribute("stroke-width", "2")
                    setAttribute("stroke-linecap", "round")
                    setAttribute("stroke-linejoin", "round")
                }
                svg.appendChild(path)

                // Optional dots
                if (showDots) {
                    points.forEach { (x, y) ->
                        val circle = document.createElementNS(SVG_NS, "circle").apply {
                            setAttribute("cx", "$x")
                            setAttribute("cy", "$y")
                            setAttribute("r", "3")
                            setAttribute("fill", colorHex)
                        }
                        svg.appendChild(circle)
                    }
                }

                // Highlight last point (most recent)
                if (points.isNotEmpty()) {
                    val (lastX, lastY) = points.last()
                    val circle = document.createElementNS(SVG_NS, "circle").apply {
                        setAttribute("cx", "$lastX")
                        setAttribute("cy", "$lastY")
                        setAttribute("r", "3")
                        setAttribute("fill", colorHex)
                        setAttribute("stroke", "#FFFFFF")
                        setAttribute("stroke-width", "1.5")
                    }
                    svg.appendChild(circle)
                }

                container.appendChild(svg)

                onDispose {
                    while (container.firstChild != null) {
                        container.removeChild(container.firstChild!!)
                    }
                }
            }
        }
    }
}

/**
 * Sparkline with fill area
 */
@Composable
fun SparklineArea(
    values: List<Int>,
    color: CSSColorValue = Colors.Primary,
    width: Int = 80,
    height: Int = 24
) {
    if (values.isEmpty()) return

    val reversedValues = values.reversed()

    val minValue = 1
    val maxValue = 12
    val valueRange = maxValue - minValue

    val stepX = width.toDouble() / (reversedValues.size - 1).coerceAtLeast(1)
    val points = reversedValues.mapIndexed { index, value ->
        val x = index * stepX
        val normalizedY = (value - minValue).toDouble() / valueRange
        val y = height - (normalizedY * height * 0.8) - (height * 0.1)
        Pair(x, y)
    }

    // Build path for line
    val linePath = points.mapIndexed { index, (x, y) ->
        if (index == 0) "M $x $y" else "L $x $y"
    }.joinToString(" ")

    // Build path for filled area
    val areaPath = buildString {
        append("M 0 $height ")
        points.forEach { (x, y) -> append("L $x $y ") }
        append("L $width $height Z")
    }

    val colorHex = "#00A651"
    val gradientId = "sparklineGradient-${values.hashCode()}"

    Div {
        Div({
            attr("data-sparkline-area", "true")
        }) {
            DisposableEffect(values, width, height) {
                val container = scopeElement

                // Clear existing SVG
                while (container.firstChild != null) {
                    container.removeChild(container.firstChild!!)
                }

                // Create SVG element
                val svg = document.createElementNS(SVG_NS, "svg").apply {
                    setAttribute("width", "$width")
                    setAttribute("height", "$height")
                    setAttribute("viewBox", "0 0 $width $height")
                }

                // Gradient definition
                val defs = document.createElementNS(SVG_NS, "defs")
                val gradient = document.createElementNS(SVG_NS, "linearGradient").apply {
                    setAttribute("id", gradientId)
                    setAttribute("x1", "0%")
                    setAttribute("y1", "0%")
                    setAttribute("x2", "0%")
                    setAttribute("y2", "100%")
                }
                val stop1 = document.createElementNS(SVG_NS, "stop").apply {
                    setAttribute("offset", "0%")
                    setAttribute("stop-color", colorHex)
                    setAttribute("stop-opacity", "0.3")
                }
                val stop2 = document.createElementNS(SVG_NS, "stop").apply {
                    setAttribute("offset", "100%")
                    setAttribute("stop-color", colorHex)
                    setAttribute("stop-opacity", "0.05")
                }
                gradient.appendChild(stop1)
                gradient.appendChild(stop2)
                defs.appendChild(gradient)
                svg.appendChild(defs)

                // Filled area
                val area = document.createElementNS(SVG_NS, "path").apply {
                    setAttribute("d", areaPath)
                    setAttribute("fill", "url(#$gradientId)")
                }
                svg.appendChild(area)

                // Line
                val path = document.createElementNS(SVG_NS, "path").apply {
                    setAttribute("d", linePath)
                    setAttribute("fill", "none")
                    setAttribute("stroke", colorHex)
                    setAttribute("stroke-width", "2")
                    setAttribute("stroke-linecap", "round")
                    setAttribute("stroke-linejoin", "round")
                }
                svg.appendChild(path)

                container.appendChild(svg)

                onDispose {
                    while (container.firstChild != null) {
                        container.removeChild(container.firstChild!!)
                    }
                }
            }
        }
    }
}
